import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { FUEL_LOGS_LIST, FUEL_LOG_DELETE } from '../../graphql/fuelLogs';
import { VEHICLES_LIST } from '../../graphql/vehicles';
import { GUARDS_LIST } from '../../graphql/guards';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, CircularProgress, TextField, MenuItem, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Pagination from '../../components/Pagination';

const FuelLogList = ({ onEdit }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get pagination and filter state from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const itemsPerPage = parseInt(searchParams.get('limit')) || 10;
  const search = searchParams.get('search') || '';
  const driver = searchParams.get('driver') || 'All';
  const vehicle = searchParams.get('vehicle') || 'All';

  // Update URL parameters
  const updateURLParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'All' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Handle filter changes
  const handleSearchChange = (value) => {
    updateURLParams({ search: value, page: 1 });
  };

  const handleDriverChange = (value) => {
    updateURLParams({ driver: value, page: 1 });
  };

  const handleVehicleChange = (value) => {
    updateURLParams({ vehicle: value, page: 1 });
  };

  const handlePageChange = (page) => {
    updateURLParams({ page });
  };

  const handleItemsPerPageChange = (limit) => {
    updateURLParams({ limit, page: 1 });
  };

  const { data, loading, error, refetch } = useQuery(FUEL_LOGS_LIST, {
    variables: { 
      first: itemsPerPage,
      skip: (currentPage - 1) * itemsPerPage,
      sort: { createdAt: 'DESC' } 
    }
  });
  const { data: vehiclesData } = useQuery(VEHICLES_LIST);
  const { data: guardsData } = useQuery(GUARDS_LIST);
  const [deleteFuelLog] = useMutation(FUEL_LOG_DELETE, {
    onCompleted: () => refetch(),
  });

  const [deleteDialog, setDeleteDialog] = useState({ open: false, log: null });

  const handleDeleteClick = (log) => {
    setDeleteDialog({ open: true, log });
  };

  const handleDeleteConfirm = async () => {
    await deleteFuelLog({ variables: { filter: { id:  deleteDialog.log.id  } } });
    setDeleteDialog({ open: false, log: null });
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, log: null });
  };

  const filteredLogs = useMemo(() => {
    if (!data?.fuelLogsList?.items) return [];
    return data.fuelLogsList.items.filter((l) => {
      const vehicleName = l.vehicle ? `${l.vehicle.make} ${l.vehicle.model} (${l.vehicle.licensePlate})` : '';
      const matchesSearch =
        (l.fuelStation && l.fuelStation.toLowerCase().includes(search.toLowerCase())) ||
        (l.notes && l.notes.toLowerCase().includes(search.toLowerCase())) ||
        vehicleName.toLowerCase().includes(search.toLowerCase());
      const matchesVehicle = vehicle === 'All' || (l.vehicle && l.vehicle.id === vehicle);
      const matchesDriver = driver === 'All' || (l.driver && l.driver.id === driver);
      return matchesSearch && matchesVehicle && matchesDriver;
    });
  }, [data, search, vehicle, driver]);

  if (loading) return <CircularProgress />;
  if (error) return <div>Error loading fuel logs.</div>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
          placeholder="Search by fuel station, notes, or vehicle"
          size="small"
        />
        <TextField
          select
          label="Vehicle"
          value={vehicle}
          onChange={e => handleVehicleChange(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="All">All Vehicles</MenuItem>
          {vehiclesData?.vehiclesList?.items.map((v) => (
            <MenuItem key={v.id} value={v.id}>
              {v.make} {v.model} ({v.licensePlate})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Driver"
          value={driver}
          onChange={e => handleDriverChange(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="All">All Drivers</MenuItem>
          {guardsData?.employeesList?.items.map((g) => (
            <MenuItem key={g.id} value={g.id}>{g.firstName} {g.lastName}</MenuItem>
          ))}
        </TextField>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Vehicle</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Gallons</TableCell>
              <TableCell>Cost</TableCell>
              <TableCell>Odometer</TableCell>
              <TableCell>Fuel Station</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.map((log) => (
              <TableRow key={log.id} hover onClick={() => onEdit(log)}>
                <TableCell>{log.vehicle ? `${log.vehicle.make} ${log.vehicle.model} (${log.vehicle.licensePlate})` : '-'}</TableCell>
                <TableCell>{log.date}</TableCell>
                <TableCell>{log.gallons}</TableCell>
                <TableCell>{log.cost}</TableCell>
                <TableCell>{log.odometer}</TableCell>
                <TableCell>{log.fuelStation}</TableCell>
                <TableCell>{log.notes}</TableCell>
                <TableCell>
                  <IconButton onClick={e => { e.stopPropagation(); onEdit(log); }} size="small" sx={{color: '#000'}}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={e => { e.stopPropagation(); handleDeleteClick(log); }} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil((data?.fuelLogsList?.count || 0) / itemsPerPage)}
        totalItems={data?.fuelLogsList?.count || 0}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        itemsPerPageOptions={[10, 25, 50, 100]}
      />
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Fuel Log</DialogTitle>
        <DialogContent>Are you sure you want to delete this fuel log?</DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FuelLogList; 